import React, { useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchForms } from '../../reducers/formsSlice';
import { fetchSubmissions } from '../../reducers/submissionsSlice';
import SurveyCard from '../../components/cards/FormCard';
import SurveyListPageSkeleton from '../../components/skeletons/FormListPageSkeleton';
import { useSync } from '../../hooks/useSync';
import DataListContainer from '../../components/layout/DataListContainer';
import PageLayout from '../../components/layout/PageLayout';

const CompletedForms = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { forms, status: formsStatus } = useSelector(state => state.forms);
  const { items: submissions, status: submissionsStatus } = useSelector(state => state.submissions);
  const { isOnline } = useSync();
  
  useEffect(() => {
    dispatch(fetchForms(isOnline)); 
    dispatch(fetchSubmissions({ page: 1, limit: 100, forceRefresh: true }));
  }, [dispatch, isOnline]);

  const completedForms = useMemo(() => {
    const counts = {};
    submissions.forEach(sub => {
      const fId = String(sub.form_id);
      counts[fId] = (counts[fId] || 0) + 1;
    });

    return forms
      .map(form => ({
        ...form,
        calculated_count: counts[String(form.id)] || 0
      }))
      .filter(form => form.submissions_exists === true || form.calculated_count > 0)
      .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
  }, [forms, submissions]);

  const handleViewForm = formId => {
    navigate(`/forms/data?formId=${formId}`, { 
      state: { 
        from: '/forms/completed',
        filter: 'completed'
      } 
    });
  };

  const isLoading = formsStatus === 'loading' || submissionsStatus === 'loading';

  return (
    <PageLayout title="Completed Forms" showBackButton showStatus>
      <DataListContainer
        isLoading={isLoading}
        data={completedForms}
        skeletonComponent={<SurveyListPageSkeleton />}
        emptyStateProps={{
          title: "No Completed Forms",
          message: "Forms that have been completed will appear here."
        }}
      >
        <div className="grid grid-cols-1 gap-4">
          {completedForms.map(form => (
            <SurveyCard
              key={form.id}
              title={form.title || form.name}
              projectId={form.project_id}
              projectName={form.project?.name}
              description={form.description}
              status={form.status}
              submissionsExists={form.submissions_exists || form.calculated_count > 0} 
              onViewLists={() => handleViewForm(form.id)}
              page='default'
            />
          ))}
        </div>
      </DataListContainer>
    </PageLayout>
  );
};

export default CompletedForms;